import React, { useState } from 'react';
import { 
  Accessibility, Eye, Type, Zap, RotateCcw, X, Check,
  Sun, Moon, ZoomIn, ZoomOut, Volume2
} from 'lucide-react';
import { useAccessibility } from '../context/AccessibilityContext';

const AccessibilityPanel = ({ isOpen, onClose }) => {
  const { 
    settings, 
    toggleHighContrast, 
    toggleLargeText, 
    toggleReducedMotion,
    setFontSize,
    resetSettings 
  } = useAccessibility();

  const fontSizes = [
    { id: 'small', label: 'Small', scale: '14px' },
    { id: 'normal', label: 'Normal', scale: '16px' },
    { id: 'large', label: 'Large', scale: '18px' },
    { id: 'xlarge', label: 'Extra Large', scale: '20px' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div 
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-labelledby="accessibility-title"
        aria-modal="true"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
              <Accessibility className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            </div>
            <h2 id="accessibility-title" className="text-lg font-semibold text-gray-900 dark:text-white">
              Accessibility Settings
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Close accessibility panel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* High Contrast Mode */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Visual Settings
            </h3>
            
            <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <div className="flex items-center gap-3">
                <Eye className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">High Contrast</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Increase color contrast for better visibility</p>
                </div>
              </div>
              <button
                onClick={toggleHighContrast}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  settings.highContrast ? 'bg-teal-600' : 'bg-gray-300 dark:bg-gray-600'
                }`}
                role="switch"
                aria-checked={settings.highContrast}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  settings.highContrast ? 'translate-x-7' : 'translate-x-1'
                }`} />
              </button>
            </label>

            <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <div className="flex items-center gap-3">
                <Type className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Large Text</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Increase text size across the site</p>
                </div>
              </div>
              <button
                onClick={toggleLargeText}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  settings.largeText ? 'bg-teal-600' : 'bg-gray-300 dark:bg-gray-600'
                }`}
                role="switch"
                aria-checked={settings.largeText}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  settings.largeText ? 'translate-x-7' : 'translate-x-1'
                }`} />
              </button>
            </label>

            <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Reduced Motion</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Minimize animations and transitions</p>
                </div>
              </div>
              <button
                onClick={toggleReducedMotion}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  settings.reducedMotion ? 'bg-teal-600' : 'bg-gray-300 dark:bg-gray-600'
                }`}
                role="switch"
                aria-checked={settings.reducedMotion}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  settings.reducedMotion ? 'translate-x-7' : 'translate-x-1'
                }`} />
              </button>
            </label>
          </div>

          {/* Font Size */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Text Size
            </h3>
            <div className="grid grid-cols-4 gap-2">
              {fontSizes.map((size) => (
                <button
                  key={size.id}
                  onClick={() => setFontSize(size.id)}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    settings.fontSize === size.id
                      ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <span 
                    className={`block font-medium ${
                      settings.fontSize === size.id ? 'text-teal-600 dark:text-teal-400' : 'text-gray-700 dark:text-gray-300'
                    }`}
                    style={{ fontSize: size.scale }}
                  >
                    Aa
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 block">
                    {size.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Preview
            </h3>
            <div className={`p-4 rounded-xl border-2 border-gray-200 dark:border-gray-600 ${
              settings.highContrast ? 'bg-black text-white' : 'bg-gray-50 dark:bg-gray-700'
            }`}>
              <p className={`font-semibold mb-2 ${settings.highContrast ? 'text-yellow-400' : 'text-gray-900 dark:text-white'}`}>
                Sample Heading
              </p>
              <p className={`text-sm ${settings.highContrast ? 'text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                This is how text will appear with your current accessibility settings. 
                The changes are applied site-wide for better readability.
              </p>
              <button className={`mt-3 px-4 py-2 rounded-lg text-sm font-medium ${
                settings.highContrast 
                  ? 'bg-yellow-400 text-black' 
                  : 'bg-teal-600 text-white'
              }`}>
                Sample Button
              </button>
            </div>
          </div>

          {/* Reset Button */}
          <button
            onClick={resetSettings}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset to Default Settings
          </button>
        </div>
      </div>
    </div>
  );
};

// Floating Accessibility Button
export const AccessibilityButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="fixed bottom-20 sm:bottom-6 left-4 z-30 p-3 bg-teal-600 hover:bg-teal-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all group"
    aria-label="Open accessibility settings"
  >
    <Accessibility className="w-5 h-5" />
    <span className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
      Accessibility
    </span>
  </button>
);

export default AccessibilityPanel;
